import { apiUrl, exportData, getList } from './apiUtils';
import type { ApiListParamsWithUnderscores, ApiExportParams } from '../types/api/listParams';
import type { ListApiResponse } from '../types/apiResponse';

const baseUrl = apiUrl + '/reports/';
const exportBaseUrl = `${apiUrl}/export/reports/`;

export function getRequestsByWeekByGame(
  year: number,
  company?: string,
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}requests/week/game?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function getRequestsByWeekBySKU(year: number, company?: string, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}requests/week/sku?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function getKeysByWeekByGame(year: number, company?: string, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}keys/week/game?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function getKeysByWeekBySku(year: number, company?: string, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}keys/week/sku?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function getKeysByWeekByCompany(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}keys/week/company?year=${year}`, params);
}

export function getRequestsByWeekByCompany(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}requests/week/company?year=${year}`, params);
}

export function getRequestsByWeekByAuthorisingUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}requests/week/authorisinguser?year=${year}`, params);
}

export function exportRequestsByWeekByAuthorisingUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}requests/week/authorisinguser?year=${year}`, 'fullName', params);
}

export function getKeysByWeekByAuthorisingUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}keys/week/authorisinguser?year=${year}`, params);
}

export function exportKeysByWeekByAuthorisingUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}keys/week/authorisinguser?year=${year}`, 'fullName', params);
}

export function getRequestsByWeekByUploadingUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}requests/week/uploadinguser?year=${year}`, params);
}

export function exportRequestsByWeekByUploadingUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}requests/week/uploadinguser?year=${year}`, 'fullName', params);
}

export function getKeysByWeekByUploadingUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}keys/week/uploadinguser?year=${year}`, params);
}

export function exportKeysByWeekByUploadingUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}keys/week/uploadinguser?year=${year}`, 'fullName', params);
}

export function exportRequestsByWeekByGame(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}requests/week/game?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'game', params ?? { filters: {}, scopes: {} });
}

export function exportRequestsByWeekBySKU(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}requests/week/sku?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'game', params ?? { filters: {}, scopes: {} });
}

export function exportKeysByWeekByGame(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}keys/week/game?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'game', params ?? { filters: {}, scopes: {} });
}

export function exportKeysByWeekBySku(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}keys/week/sku?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'game', params ?? { filters: {}, scopes: {} });
}

export function exportKeysByWeekByCompany(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}keys/week/company?year=${year}`, 'companyName', params);
}

export function exportRequestsByWeekByCompany(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}requests/week/company?year=${year}`, 'companyName', params);
}

export function exportSLAsByCompany(params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}sla/company`, 'companyName', params);
}

export function getUsersModifiedByWeekByUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}users/week/user?year=${year}`, params);
}

export function exportUsersModifiedByWeekByUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}users/week/user?year=${year}`, 'fullName', params);
}

export function getKeyWorkflowsModifiedByWeekByUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}keyworkflows/week/user?year=${year}`, params);
}

export function exportKeyWorkflowsModifiedByWeekByUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}keyworkflows/week/user?year=${year}`, 'fullName', params);
}

export function getGamesModifiedByWeekByUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}games/week/user?year=${year}`, params);
}

export function exportGamesModifiedByWeekByUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}games/week/user?year=${year}`, 'fullName', params);
}

export function getSKUsModifiedByWeekByUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}skus/week/user?year=${year}`, params);
}

export function exportSKUsModifiedByWeekByUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}skus/week/user?year=${year}`, 'fullName', params);
}

export function getRequestsByWeekByWorkflow(year: number, company?: string, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}requests/week/workflow?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function exportRequestsByWeekByWorkflow(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}requests/week/workflow?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'workflow', params ?? { filters: {}, scopes: {} });
}
export function getKeysByWeekByWorkflow(year: number, company?: string, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}keys/week/workflow?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function exportKeysByWeekByWorkflow(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}keys/week/workflow?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'workflow', params ?? { filters: {}, scopes: {} });
}
